<script setup>
// NOTICE: KAPTION-INTEGRATION - Sidebar with close functionality and Kaption Copilot
import { computed, ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';
import KaptionCopilotPanel from 'dashboard/components/kaption/KaptionCopilotPanel.vue';
// NOTICE: KAPTION-INTEGRATION - Dashboard app icon support
import DashboardAppIcon from 'dashboard/components/DashboardApp/DashboardAppIcon.vue';
// NOTE: Badge removed from tabs - now shown on floating sidebar toggle button (SidepanelSwitch.vue)
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { canUseFeature } = useFeaturePermissions();

// NOTICE: KAPTION-INTEGRATION - Resizable sidebar
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const isResizing = ref(false);
const sidebarRef = ref(null);

// Get stored width or use default
const sidebarWidth = computed(() => {
  const storedWidth = uiSettings.value.sidebar_width;
  if (storedWidth && storedWidth >= MIN_WIDTH && storedWidth <= MAX_WIDTH) {
    return storedWidth;
  }
  return MIN_WIDTH;
});

// Handle resize movement (defined first to avoid reference errors)
const handleResize = event => {
  if (!isResizing.value || !sidebarRef.value) return;

  const sidebarRect = sidebarRef.value.getBoundingClientRect();
  // Calculate new width based on mouse position (sidebar is on the right)
  const newWidth = sidebarRect.right - event.clientX;

  // Clamp to min/max
  const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

  // Update immediately for smooth resizing
  sidebarRef.value.style.width = `${clampedWidth}px`;
  sidebarRef.value.style.minWidth = `${clampedWidth}px`;
};

// Stop resize and persist (defined before startResize)
const stopResize = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  // NOTICE: KAPTION-INTEGRATION - chat-ui-fixes.RESIZE_SHIELD.4
  window.removeEventListener('blur', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';

  // Get final width and persist
  if (sidebarRef.value) {
    const finalWidth = parseInt(sidebarRef.value.style.width, 10);
    if (finalWidth >= MIN_WIDTH && finalWidth <= MAX_WIDTH) {
      updateUISettings({ sidebar_width: finalWidth });
    }
  }
};

// Start resize
const startResize = event => {
  event.preventDefault();
  isResizing.value = true;
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  // NOTICE: KAPTION-INTEGRATION - End the drag cleanly if the window loses
  // focus mid-drag (e.g. cmd-tab), instead of staying stuck to the mouse.
  // SID: chat-ui-fixes.RESIZE_SHIELD.4
  window.addEventListener('blur', stopResize);
  document.body.style.cursor = 'ew-resize';
  document.body.style.userSelect = 'none';
};

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  window.removeEventListener('blur', stopResize);
});

// NOTICE: KAPTION-INTEGRATION - Check if Kaption Copilot is enabled
// feature-permissions.AI.3 - gate on flag + client-managed permission
const isKaptionCopilotEnabled = computed(
  () => isKaptionFlagEnabled('KAPTION_COPILOT') && canUseFeature('copilot')
);

// NOTICE: KAPTION-INTEGRATION - Get only sidebar apps
const allDashboardApps = useMapGetter('dashboardApps/getRecords');
// feature-permissions.DYNAMIC.1 - also hide apps the user may not use (instant; BE index filters too)
const sidebarApps = computed(() =>
  allDashboardApps.value.filter(
    app =>
      app.placement === 'sidebar' && canUseFeature(`dashboard_app:${app.id}`)
  )
);

// NOTICE: KAPTION-INTEGRATION - Calculate total tabs (contact + copilot if enabled + sidebar apps)
const totalTabs = computed(() => {
  let count = 1; // Contact tab is always present
  if (isKaptionCopilotEnabled.value) {
    count += 1; // Copilot tab
  }
  count += sidebarApps.value.length; // Sidebar apps
  return count;
});

// NOTICE: KAPTION-INTEGRATION - Tab indices: 0 = Contact, 1 = Copilot (if enabled), 2+ = Sidebar apps
const copilotTabIndex = computed(() =>
  isKaptionCopilotEnabled.value ? 1 : -1
);
const sidebarAppStartIndex = computed(() =>
  isKaptionCopilotEnabled.value ? 2 : 1
);

const activeTab = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - Sidebar tab logic
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    sidebar_app_tab: sidebarAppTab = 0,
  } = uiSettings.value;

  // Default to true (open) if never explicitly set by user
  if (isContactSidebarOpen ?? true) {
    // NOTICE: KAPTION-INTEGRATION - Reset to contact tab (0) if stored tab is out of bounds
    const maxTab = totalTabs.value - 1;
    if (sidebarAppTab > maxTab) {
      return 0;
    }
    return sidebarAppTab;
  }
  return null;
});

// NOTICE: KAPTION-INTEGRATION - Close button handler
const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

// NOTICE: KAPTION-INTEGRATION
const onTabChange = index => {
  updateUISettings({
    sidebar_app_tab: index,
  });
  // NOTICE: KAPTION-INTEGRATION - Clear badge when sidebar tab is activated
  // Use sidebarAppStartIndex to calculate correct app index
  if (index >= sidebarAppStartIndex.value) {
    const appIndex = index - sidebarAppStartIndex.value;
    const app = sidebarApps.value[appIndex];
    if (app?.id) {
      store.dispatch('dashboardAppBadges/clearAppNotifications', {
        appId: app.id,
      });
    }
  }
};
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - Resizable sidebar with close functionality -->
  <div
    ref="sidebarRef"
    class="wp-card-md wp-nodivider bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm ltr:right-0 rtl:left-0 md:relative md:top-auto ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab !== null,
        'md:hidden': activeTab === null,
      },
    ]"
    :style="{
      width: `${sidebarWidth}px`,
      minWidth: `${sidebarWidth}px`,
    }"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - Transparent full-viewport shield while the
         resize drag is active. Dashboard-app iframes swallow mousemove/mouseup,
         so without this the drag never receives the release and stays stuck to
         the mouse. The shield sits above everything (incl. iframes), letting the
         document-level drag listeners keep working; it exists ONLY during a drag.
         SID: chat-ui-fixes.RESIZE_SHIELD.1 chat-ui-fixes.RESIZE_SHIELD.2 chat-ui-fixes.RESIZE_SHIELD.3 -->
    <Teleport to="body">
      <div
        v-if="isResizing"
        data-test-id="sidebar-resize-shield"
        class="fixed inset-0 z-[9999] cursor-ew-resize"
      />
    </Teleport>
    <!-- NOTICE: KAPTION-INTEGRATION - Resize handle (left edge for LTR, right edge for RTL) -->
    <div
      class="hidden md:flex absolute top-0 bottom-0 w-3 cursor-ew-resize z-50 ltr:left-0 rtl:right-0 ltr:-translate-x-1/2 rtl:translate-x-1/2 items-center justify-center group"
      @mousedown="startResize"
    >
      <!-- Visible grip indicator - subtle with hover/active states, works in light/dark mode -->
      <div
        class="h-8 w-1 rounded-full transition-all duration-150"
        :class="
          isResizing
            ? 'bg-woot-500 h-12'
            : 'bg-n-slate-6 dark:bg-n-slate-8 group-hover:bg-woot-400 group-hover:h-10'
        "
      />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - Add tabs for sidebar apps and Kaption Copilot -->
    <div
      v-if="sidebarApps.length || isKaptionCopilotEnabled || activeTab !== null"
      class="border-b border-n-weak flex items-center justify-between px-2"
    >
      <div class="flex gap-0 flex-1">
        <!-- Contact tab (always index 0) -->
        <button
          class="flex-1 py-2 px-2 text-sm font-medium text-center border-b-2 transition-colors truncate flex items-center justify-center gap-1 rounded-t-md"
          :class="
            activeTab === 0
              ? 'border-b-woot-500 text-woot-600 bg-woot-25 dark:bg-woot-800/20'
              : 'border-b-transparent text-n-text-weak hover:text-n-text hover:bg-n-alpha-1'
          "
          :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
          @click="onTabChange(0)"
        >
          <span class="i-lucide-contact size-4" />
          {{ $t('CONVERSATION.SIDEBAR.CONTACT') }}
        </button>
        <!-- NOTICE: KAPTION-INTEGRATION - Kaption Copilot tab (index 1 if enabled) with purple copilot color -->
        <button
          v-if="isKaptionCopilotEnabled"
          class="flex-1 py-2 px-2 text-sm font-medium text-center border-b-2 transition-colors truncate flex items-center justify-center gap-1 rounded-t-md"
          :class="
            activeTab === copilotTabIndex
              ? 'border-b-kpt-copilot text-kpt-copilot bg-kpt-copilot/10'
              : 'border-b-transparent text-n-text-weak hover:text-n-text hover:bg-n-alpha-1'
          "
          :title="$t('CONVERSATION.SIDEBAR.COPILOT')"
          @click="onTabChange(copilotTabIndex)"
        >
          <span class="i-woot-copilot-sparkles size-4" />
          {{ $t('CONVERSATION.SIDEBAR.COPILOT') }}
        </button>
        <!-- Sidebar apps tabs -->
        <button
          v-for="(app, index) in sidebarApps"
          :key="'sidebar-tab-' + app.id"
          class="flex-1 py-2 px-2 text-sm font-medium text-center border-b-2 transition-colors truncate flex items-center justify-center gap-1.5 relative rounded-t-md"
          :class="
            activeTab === sidebarAppStartIndex + index
              ? 'border-b-woot-500 text-woot-600 bg-woot-25 dark:bg-woot-800/20'
              : 'border-b-transparent text-n-text-weak hover:text-n-text hover:bg-n-alpha-1'
          "
          :title="app.sidebar_settings?.label || app.title"
          @click="onTabChange(sidebarAppStartIndex + index)"
        >
          <!-- NOTICE: KAPTION-INTEGRATION - Dashboard app icon in sidebar tab -->
          <DashboardAppIcon
            :icon="app.icon"
            :title="app.sidebar_settings?.label || app.title"
            :size="16"
          />
          <span class="truncate">{{
            app.sidebar_settings?.label || app.title
          }}</span>
          <!-- NOTE: Badge moved to floating sidebar toggle button (SidepanelSwitch.vue) -->
        </button>
      </div>
      <!-- NOTICE: KAPTION-INTEGRATION - Close button in tab header -->
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <button
        class="p-1 text-n-text-weak hover:text-n-text transition-colors flex-shrink-0 text-lg leading-none"
        :title="$t('TOOLTIP.CLOSE')"
        :aria-label="$t('TOOLTIP.CLOSE')"
        @click="closeContactPanel"
        v-html="'&times;'"
      />
    </div>
    <div class="flex flex-1 overflow-auto relative">
      <!-- NOTICE: KAPTION-INTEGRATION - Show contact panel when tab is 0 -->
      <ContactPanel
        v-show="activeTab === 0 || activeTab === null"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Kaption Copilot panel -->
      <KaptionCopilotPanel
        v-if="isKaptionCopilotEnabled"
        v-show="activeTab === copilotTabIndex"
        :conversation-id="currentChat.id"
        :current-chat="currentChat"
        class="w-full h-full"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Sidebar app frames using CSS positioning -->
      <!-- Iframes are created immediately and positioned off-screen until their tab is active -->
      <!-- Using position/opacity instead of display:none to not interrupt iframe loading -->
      <DashboardAppFrame
        v-for="(app, index) in sidebarApps"
        v-show="activeTab === sidebarAppStartIndex + index"
        :key="'sidebar-' + app.id"
        :config="app.content"
        :current-chat="currentChat"
        :is-visible="activeTab === sidebarAppStartIndex + index"
        :position="index"
        :dashboard-app-id="app.id"
        surface="sidebar"
        class="sidebar-app-frame"
        :class="{
          'sidebar-app-frame--active':
            activeTab === sidebarAppStartIndex + index,
          'sidebar-app-frame--hidden':
            activeTab !== sidebarAppStartIndex + index,
        }"
      />
    </div>
  </div>
</template>

<style scoped>
/* NOTICE: KAPTION-INTEGRATION - Sidebar app frame positioning */
/* Using position/opacity instead of display:none to preserve iframe loading state */
.sidebar-app-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sidebar-app-frame--active {
  /* Active frame is visible and positioned normally */
  opacity: 1;
  visibility: visible;
  z-index: 1;
}

.sidebar-app-frame--hidden {
  /* Hidden frames are positioned off-screen but still render */
  /* This allows iframes to load and maintain state without display:none */
  opacity: 0;
  visibility: hidden;
  z-index: 0;
  pointer-events: none;
}
</style>
